import React, { useEffect, useMemo, useState } from 'react';
import { Dialog } from '@headlessui/react';

export interface StepJumpControlStep {
  id: number;
  order: number;
  title: string;
  stage_tag: string | null;
  step_kind: string;
  counts_as_evidence: boolean;
}

interface StepJumpControlProps {
  open: boolean;
  steps: StepJumpControlStep[];
  currentStepId: number | null;
  stepNavLoadingId: number | null;
  onClose: () => void;
  onSelectStep: (step: StepJumpControlStep) => void;
}

export const StepJumpControl: React.FC<StepJumpControlProps> = ({
  open,
  steps,
  currentStepId,
  stepNavLoadingId,
  onClose,
  onSelectStep,
}) => {
  const [query, setQuery] = useState('');

  useEffect(() => {
    if (!open) {
      setQuery('');
    }
  }, [open]);

  const filteredSteps = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return steps;
    return steps.filter((step) => {
      const title = String(step.title || '').toLowerCase();
      const stage = String(step.stage_tag || '').toLowerCase();
      return title.includes(q) || stage.includes(q) || String(step.order).includes(q);
    });
  }, [query, steps]);

  return (
    <Dialog
      open={open}
      onClose={() => {
        if (stepNavLoadingId !== null) return;
        onClose();
      }}
      className="relative z-50"
    >
      <div className="fixed inset-0 bg-black/40" aria-hidden="true" />
      <div className="fixed inset-0 flex items-center justify-center p-6">
        <Dialog.Panel className="w-full max-w-lg rounded-xl bg-white shadow-xl">
          <div className="p-6 space-y-4">
            <Dialog.Title className="text-lg font-semibold text-gray-900">Jump to step</Dialog.Title>
            <p className="text-sm text-gray-600">
              Select a step to move the class.
            </p>
            <input
              type="search"
              className="w-full rounded border border-gray-200 px-3 py-2 text-sm text-gray-700"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search by title, stage, or step number"
              disabled={stepNavLoadingId !== null}
            />
            <div className="max-h-80 space-y-2 overflow-y-auto">
              {filteredSteps.length === 0 ? (
                <div className="rounded border border-dashed border-gray-200 px-3 py-6 text-center text-sm text-gray-500">
                  No steps match your search.
                </div>
              ) : (
                filteredSteps.map((step) => (
                  <button
                    key={`jump-modal-step-${step.id}`}
                    type="button"
                    onClick={() => onSelectStep(step)}
                    disabled={currentStepId === step.id || stepNavLoadingId !== null}
                    className={`w-full rounded border px-3 py-2 text-left transition disabled:cursor-not-allowed disabled:opacity-70 ${
                      currentStepId === step.id
                        ? 'border-indigo-500 bg-indigo-50'
                        : 'border-gray-200 bg-white hover:border-indigo-300 hover:bg-indigo-50/40'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <p className="text-xs text-gray-500">Step {step.order}</p>
                      <p className="text-xs text-gray-400">{step.stage_tag || 'class'}</p>
                    </div>
                    <p className="mt-1 text-sm font-semibold text-gray-900">{step.title || 'Untitled step'}</p>
                  </button>
                ))
              )}
            </div>
            <div className="flex justify-end">
              <button
                type="button"
                className="rounded border border-gray-200 px-3 py-1.5 text-sm text-gray-700 disabled:opacity-50"
                onClick={onClose}
                disabled={stepNavLoadingId !== null}
              >
                Close
              </button>
            </div>
          </div>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
};
